import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Adaeze Okonkwo',
    initials: 'AO',
    color: '#C9956C',
    service: 'Bridal Makeup & Hair',
    rating: 5,
    text: "Lamisking gave me the most beautiful bridal look I could have ever imagined! My hair was perfect and the makeup lasted the entire day and night. Every guest at my wedding asked where I got my glam done. I tell everyone — Lamisking is THE place in Abuja. Truly five-star service.",
  },
  {
    name: 'Chisom Nwachukwu',
    initials: 'CN',
    color: '#6B2737',
    service: 'Natural Hair Treatment',
    rating: 5,
    text: "I've been struggling with dry, damaged hair for years. One visit to Lamisking completely changed my hair journey. The deep conditioning treatment was incredible — my hair has never felt this moisturized and healthy. The staff are so knowledgeable about natural hair care.",
  },
  {
    name: 'Fatima Abdullahi',
    initials: 'FA',
    color: '#8B4A2A',
    service: 'Full Body Massage & Spa',
    rating: 5,
    text: "The spa experience at Lamisking is absolutely world-class. I came in completely stressed from work and left feeling like a new woman. The hot stone massage was divine, and the ambiance is so calming. This is my monthly ritual now — I refuse to miss it!",
  },
  {
    name: 'Blessing Eze',
    initials: 'BE',
    color: '#C9956C',
    service: 'Box Braids & Styling',
    rating: 5,
    text: "Got my box braids done here last week and the quality is exceptional! They took their time, the braids are neat, uniform and so beautiful. My scalp didn't hurt at all — which is rare! The stylists are clearly trained and passionate about their craft. Won't go anywhere else!",
  },
  {
    name: 'Ngozi Amadi',
    initials: 'NA',
    color: '#6B2737',
    service: 'Facials & Skincare',
    rating: 5,
    text: "The facial treatment I received was absolutely luxurious. My skin glowed for weeks afterwards! The esthetician took time to understand my skin concerns and customized the treatment perfectly. Lamisking truly lives up to its premium reputation — every detail screams excellence.",
  },
];

function Avatar({ initials, color }) {
  return (
    <div
      className="w-12 h-12 rounded-full flex items-center justify-center font-playfair font-bold text-white text-sm flex-shrink-0 shadow-md"
      style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
    >
      {initials}
    </div>
  );
}

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(count)].map((_, i) => (
        <Star key={i} className="w-3.5 h-3.5 fill-roseGold text-roseGold" />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const intervalRef = useRef(null);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });

  const startAuto = () => {
    intervalRef.current = setInterval(() => {
      setDirection(1);
      setCurrent((c) => (c + 1) % testimonials.length);
    }, 5000);
  };

  useEffect(() => {
    startAuto();
    return () => clearInterval(intervalRef.current);
  }, []);

  const goTo = (idx) => {
    setDirection(idx > current ? 1 : -1);
    setCurrent(idx);
    clearInterval(intervalRef.current);
    startAuto();
  };

  const prev = () => goTo((current - 1 + testimonials.length) % testimonials.length);
  const next = () => goTo((current + 1) % testimonials.length);

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
  };

  return (
    <section
      id="testimonials"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #2d1220 50%, #3d1528 100%)' }}
    >
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 opacity-10"
        style={{ background: 'radial-gradient(circle, #C9956C, transparent 70%)' }} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={ref} className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="font-inter text-roseGold text-xs font-semibold tracking-[0.3em] uppercase mb-3"
          >
            Client Stories
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-playfair text-4xl md:text-5xl font-bold text-champagne mb-5"
          >
            What Our Clients Say
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={inView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="w-20 h-0.5 bg-gradient-to-r from-roseGold to-burgundy mx-auto"
          />
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Large quote icon */}
          <div className="absolute -top-4 left-6 md:left-12 opacity-20">
            <Quote className="w-16 h-16 text-roseGold" />
          </div>

          {/* Card */}
          <div className="overflow-hidden rounded-3xl">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12"
              >
                {/* Stars */}
                <div className="mb-6">
                  <StarRating count={testimonials[current].rating} />
                </div>

                {/* Quote text */}
                <p className="font-inter text-white/85 text-base md:text-lg leading-relaxed mb-8 italic">
                  &ldquo;{testimonials[current].text}&rdquo;
                </p>

                {/* Client info */}
                <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                  <Avatar
                    initials={testimonials[current].initials}
                    color={testimonials[current].color}
                  />
                  <div>
                    <div className="font-playfair font-semibold text-champagne text-base">
                      {testimonials[current].name}
                    </div>
                    <div className="font-inter text-roseGold text-xs tracking-wide mt-0.5">
                      {testimonials[current].service}
                    </div>
                  </div>

                  {/* Google verified badge */}
                  <div className="ml-auto hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                    <svg viewBox="0 0 20 20" className="w-3.5 h-3.5" fill="none">
                      <path d="M18.2 10.2c0-.6-.1-1.2-.2-1.8H10v3.4h4.6c-.2 1-.8 1.9-1.7 2.4v2h2.7c1.6-1.5 2.6-3.7 2.6-6z" fill="#4285F4" />
                      <path d="M10 18c2.3 0 4.2-.8 5.6-2.1l-2.7-2c-.8.5-1.7.8-2.9.8-2.2 0-4.1-1.5-4.8-3.5H2.4v2.1C3.8 16.1 6.7 18 10 18z" fill="#34A853" />
                      <path d="M5.2 11.2c-.2-.5-.3-1-.3-1.5s.1-1 .3-1.5V6.1H2.4C1.9 7.1 1.6 8.5 1.6 10s.3 2.9.8 3.9l2.8-2.7z" fill="#FBBC05" />
                      <path d="M10 4.8c1.3 0 2.4.4 3.3 1.3l2.4-2.4C14.2 2.3 12.3 1.6 10 1.6 6.7 1.6 3.8 3.5 2.4 6.3l2.8 2.1c.7-2 2.6-3.6 4.8-3.6z" fill="#EA4335" />
                    </svg>
                    <span className="font-inter text-white/50 text-xs">Google Verified</span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === current
                      ? 'w-8 h-2 bg-roseGold'
                      : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            {/* Arrow buttons */}
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-roseGold hover:bg-roseGold/10 transition-all duration-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-roseGold hover:bg-roseGold/10 transition-all duration-300"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Bottom stat strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="grid grid-cols-3 gap-4 mt-16 pt-10 border-t border-white/10"
        >
          {[
            { num: '4.9★', label: 'Google Rating' },
            { num: '132+', label: 'Happy Clients' },
            { num: '100%', label: 'Would Recommend' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="font-playfair text-2xl md:text-3xl font-bold text-roseGold mb-1">
                {stat.num}
              </div>
              <div className="font-inter text-white/50 text-xs tracking-wide">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
